import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiMessages, readApiStatus } from "@/shared/utils/apiError";

import { CARDS_API_ENDPOINTS } from "../constants/cards.constants";
import { classifyDepositAddressesError } from "../hooks/useCardDepositAddresses";
import type { CardFundingOptions } from "../types/cardFundingOptions.types";
import { readDepositAddresses } from "../utils/cardDeposit";
import { legacyFundingOptions, type LegacyCryptoAnswer } from "../utils/cardFunding";
import { CardCopTopUpsService } from "./cardCopTopUps.service";
import { CardsService } from "./cardsService";
import { WalletService } from "./walletsService";

/**
 * A 404 for the route itself, not for the card: Nest answers an unknown route with `Cannot GET …`, while a
 * missing card carries its own sentence and must stay an error.
 */
function isMissingRoute(error: unknown): boolean {
  if (readApiStatus(error) !== 404) return false;
  const [message] = readApiMessages(error);
  return !message || message.startsWith("Cannot ");
}

export const CardFundingOptionsService = {
  /**
   * Answered for the card's organization, so staff see a client's options and card wallet. Against a backend
   * that predates the route, rebuilt from the reads it does serve (`legacyFundingOptions`), so the dashboard
   * can ship before the backend without breaking "Cargar" — the same contract as `fetchTransferTargets`.
   */
  async forCard(cardId: string, organizationId: string | null = null): Promise<CardFundingOptions> {
    try {
      const { data } = await HttpApiService.get<CardFundingOptions>(
        `${CARDS_API_ENDPOINTS.CARDS}/${cardId}/funding-options`,
      );
      return data;
    } catch (err: unknown) {
      if (!isMissingRoute(err)) throw err;
      return legacyForCard(cardId, organizationId);
    }
  },
};

async function legacyForCard(cardId: string, organizationId: string | null): Promise<CardFundingOptions> {
  const [copTerms, crypto, walletBalance] = await Promise.all([
    CardCopTopUpsService.terms(cardId).catch(() => null),
    CardsService.getCardDepositAddresses(cardId).then(
      (data): LegacyCryptoAnswer => ({ kind: "addresses", count: readDepositAddresses(data).length }),
      (err: unknown): LegacyCryptoAnswer => ({
        kind: "failure",
        failure: classifyDepositAddressesError(err),
      }),
    ),
    organizationId
      ? WalletService.getOrganizationCardWalletBalance(organizationId).then(
          (response) => response.data.balance.current.balance,
          () => null,
        )
      : Promise.resolve(null),
  ]);
  return legacyFundingOptions({ cardId, copTerms, crypto, walletBalance });
}
